import { LIMITS, newId } from './model';
const unsupported = line => { const error = new Error(`This Mermaid syntax${line ? ` on line ${line}` : ''} is not supported for editable canvas conversion. Use Render preview; the canvas was not changed.`); error.code = 'EDITABLE_UNSUPPORTED'; throw error; };
export function mermaidCapability(source) {
  const header = source.split(/\r?\n/).find(line => line.trim() && !line.trim().startsWith('%%'))?.trim() || '';
  if (/^(flowchart|graph)\b/.test(header)) return { type: 'flowchart', editable: true };
  if (/^sequenceDiagram\b/.test(header)) return { type: 'sequence', editable: false };
  const type = /^(erDiagram|gantt|pie)\b/.exec(header)?.[1];
  return { type: type || 'unsupported', editable: false };
}
// Numeric Mermaid entities keep punctuation, quotes and line breaks inside labels.
const encodeLabel = text => String(text).replace(/[#"\n\r[\]{}()|<>;:&\\]/g, c => `#${c.charCodeAt(0)};`);
// Mermaid rejects empty quoted labels. A zero-width entity represents an empty
// canvas label; decoding our output restores that empty label.
const decodeLabel = text => text === '#8203;' ? '' : text.replace(/#(\d{1,6});/g, (all, code) => Number(code) <= 0x10ffff ? String.fromCodePoint(Number(code)) : all);
const SHAPES = [
  ['((', '))', 'circle'], ['([', '])', 'ellipse'], ['[(', ')]', 'cylinder'],
  ['[[', ']]', 'predefinedProcess'], ['{{', '}}', 'hexagon'],
  ['[/', '/]', 'parallelogram'], ['[\\', '\\]', 'backwardParallelogram'],
  ['[/', '\\]', 'trapezoid'], ['[', ']', 'rectangle'], ['{', '}', 'diamond'], ['(', ')', 'rounded'],
];
const identifier = /^[A-Za-z_]\w*(?:-(?!-)[\w]+)*$/;
const groupDeclaration = /^subgraph\s+([A-Za-z_]\w*(?:-(?!-)[\w]+)*)(?:\s*\["?([^"\]]*)"?\])?$/;
const directionOf = source => /^(?:flowchart|graph)\s+(TD|TB|LR|RL|BT)$/.exec(statements(source)[0]?.text || '')?.[1] || 'TD';
// Only separators outside labels are statements. Mermaid entities contain ';'.
function statements(source) {
  const result = []; let text = '', quote = false, edgeLabel = false, comment = false, depth = 0, line = 1;
  const flush = () => { if (text.trim() && !text.trim().startsWith('%%')) result.push({ text: text.trim(), line }); text = ''; };
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (c === '%' && source[i + 1] === '%' && !text.trim()) comment = true;
    if (comment && c !== '\n') { text += c; continue; }
    if (c === '"') quote = !quote;
    if (c === '|' && !quote && depth === 0) edgeLabel = !edgeLabel;
    if (!quote && '[({'.includes(c)) depth++;
    if (!quote && '])}'.includes(c)) depth--;
    if (c === '\n' || (c === ';' && !quote && !edgeLabel && depth === 0)) { flush(); if (c === '\n') { line++; quote = false; edgeLabel = false; comment = false; depth = 0; } }
    else text += c;
  }
  flush(); return result;
}
// Intentionally bounded, editable subset, not a replacement Mermaid renderer.
// No directives, HTML, links, plugins, remote assets, or eval are interpreted.
export function fromMermaid(source) {
  if (source.length > 100000) throw new Error('Mermaid source exceeds 100,000 characters.');
  if (/^\s*%%\{/m.test(source)) throw new Error('Mermaid configuration directives are not supported. The canvas was not changed.');
  const lines = statements(source);
  const header = lines.shift()?.text;
  const flow = /^(?:flowchart|graph)\s+(TD|TB|LR|RL|BT)$/.exec(header || '');
  if (!flow) return unsupported();
  const nodes = new Map(), edges = [];
  const groups = new Set(); let group;
  const node = (key, type = 'rectangle', label) => {
    if (key.length > 100 || label?.length > LIMITS.text || groups.has(key)) return unsupported();
    if (!nodes.has(key)) nodes.set(key, { id: key, type, text: label ?? key, x: 0, y: 0, w: type === 'circle' ? 80 : 160, h: 80, fill: '#eff6ff', stroke: '#2563eb', color: '#0f172a', fontSize: 14 });
    if (group) {
      if (nodes.get(key).group && nodes.get(key).group !== group) return unsupported();
      nodes.get(key).group = group;
    }
    if (label !== undefined) Object.assign(nodes.get(key), { type, text: label, w: type === 'circle' ? 80 : 160 });
    if (nodes.size > LIMITS.shapes) throw new Error('Too many Mermaid nodes.');
    return nodes.get(key);
  };
  const readNode = (text, line) => {
    const match = /^([A-Za-z_]\w*(?:-(?!-)[\w]+)*)/.exec(text);
    if (!match) return unsupported(line);
    const rest = text.slice(match[0].length).trimStart();
    for (const [open, close, type] of SHAPES) {
      if (!rest.startsWith(open)) continue;
      const body = rest.slice(open.length);
      const quoted = body.startsWith('"');
      const end = quoted ? body.indexOf('"' + close, 1) : body.indexOf(close);
      if (end < 0) continue;
      const label = decodeLabel(body.slice(quoted ? 1 : 0, end));
      return { value: node(match[1], type, label), rest: body.slice(end + close.length + (quoted ? 1 : 0)).trim() };
    }
    return { value: node(match[1]), rest: rest.trim() };
  };
  for (const { text, line } of lines) {
    const subgraph = groupDeclaration.exec(text);
    if (subgraph) {
      if (group || groups.has(subgraph[1]) || nodes.has(subgraph[1]) || subgraph[1].length > 100) return unsupported(line);
      group = subgraph[1]; groups.add(group); continue;
    }
    if (text === 'end') {
      const closingGroup = group;
      if (!closingGroup || ![...nodes.values()].some(n => n.group === closingGroup)) return unsupported(line);
      group = undefined; continue;
    }
    if (/^(subgraph|direction|class|style|click|linkStyle)\b/.test(text)) return unsupported(line);
    {
      let { value, rest } = readNode(text.replace(/;$/, ''), line);
      while (rest) {
        const arrow = /^(<-->|<--|-->|---|<-\.->|-\.->|-\.-|<==>|==>|===)(?:\|(?:"([^"]*)"|([^|]*))\|)?\s*/.exec(rest);
        const labeled = !arrow && /^--\s+(?:"([^"]*)"|(.+?))\s+-->\s*/.exec(rest);
        if (!arrow && !labeled) return unsupported(line);
        const next = readNode(rest.slice((arrow || labeled)[0].length), line);
        const token = arrow?.[1] || '-->';
        const label = decodeLabel(arrow ? arrow[2] ?? arrow[3] ?? '' : labeled[1] ?? labeled[2]);
        if (label.length > LIMITS.text) return unsupported(line);
        edges.push({ from: value.id, to: next.value.id, text: label, arrowStart: token.startsWith('<'), arrowEnd: token.endsWith('>'), ...(token.includes('.') ? { lineStyle: 'dotted' } : token.includes('=') ? { lineStyle: 'thick' } : {}) });
        if (edges.length > LIMITS.connectors) throw new Error('Too many Mermaid connectors.');
        value = next.value; rest = next.rest;
      }
    }
    if (edges.length > LIMITS.connectors) throw new Error('Too many Mermaid connectors.');
  }
  if (group) return unsupported();
  if (!nodes.size) return { elements: [], connectors: [] };
  // Longest DAG layers keep shared targets after every incoming branch. Cycles
  // use a bounded fallback; no graph link is removed to make layout possible.
  const values = [...nodes.values()], depths = new Map(values.map(n => [n.id, 0]));
  const incoming = new Map(values.map(n => [n.id, edges.filter(e => e.to === n.id).length]));
  const queue = values.filter(n => incoming.get(n.id) === 0).map(n => n.id), visited = new Set();
  while (queue.length) { const id = queue.shift(); visited.add(id); edges.filter(e => e.from === id).forEach(e => { depths.set(e.to, Math.max(depths.get(e.to), depths.get(id) + 1)); incoming.set(e.to, incoming.get(e.to) - 1); if (incoming.get(e.to) === 0) queue.push(e.to); }); }
  let fallback = Math.max(...depths.values());
  values.filter(n => !visited.has(n.id)).forEach(n => depths.set(n.id, fallback++));
  const lanes = new Map();
  values.forEach(n => { const depth = depths.get(n.id) || 0, lane = lanes.get(depth) || 0; lanes.set(depth, lane + 1); const horizontal = ['LR', 'RL'].includes(flow[1]), sign = ['RL', 'BT'].includes(flow[1]) ? -1 : 1; n.x = horizontal ? depth * 190 * sign : lane * 190; n.y = horizontal ? lane * 130 : depth * 130 * sign; });
  const minX = Math.min(...values.map(n => n.x)), minY = Math.min(...values.map(n => n.y));
  values.forEach(n => { n.x -= minX; n.y -= minY; });
  return { elements: values, connectors: edges.map(e => ({ ...e, id: newId(), kind: 'elbow' })) };
}
export function toMermaid(elements, connectors, source = '') {
  const used = new Set(elements.map(e => e.id));
  const names = new Map(elements.map(e => {
    let name = e.id;
    if (!identifier.test(name) || ['end', 'subgraph', 'direction'].includes(name)) { name = `N_${name}`; while (used.has(name)) name = `N_${name}`; }
    used.add(name); return [e.id, name];
  }));
  if (connectors.some(c => !names.has(c.from) || !names.has(c.to))) throw new Error('A connection has a missing node. The canvas was not changed.');
  const labels = new Map(statements(source).map(s => groupDeclaration.exec(s.text)).filter(Boolean).map(m => [m[1], decodeLabel(m[2] ?? m[1])]));
  const declaration = e => {
    // Unsupported visual geometries (document/cloud/freehand, etc.) become a
    // rectangle; labels/connectivity survive. JSON remains the lossless format.
    const [open, close] = SHAPES.find(s => s[2] === e.type) || SHAPES.find(s => s[2] === 'rectangle');
    return `    ${names.get(e.id)}${open}"${encodeLabel(e.text ?? '') || '#8203;'}"${close}`;
  };
  const declarations = elements.filter(e => !e.group).map(declaration);
  for (const group of new Set(elements.map(e => e.group).filter(Boolean))) {
    let name = group; if (!identifier.test(name) || used.has(name) || ['end', 'subgraph', 'direction'].includes(name)) name = `G_${name}`;
    while (used.has(name)) name = `G_${name}`;
    used.add(name);
    declarations.push(`    subgraph ${name}["${encodeLabel(labels.get(group) ?? group)}"]`, ...elements.filter(e => e.group === group).map(declaration), '    end');
  }
  const result = [`flowchart ${directionOf(source)}`, ...declarations, ...connectors.map(c => {
    // Mermaid has no standalone left-pointing link token. Reverse the endpoints
    // for start-only arrows so the actual graph direction remains unchanged.
    const reverse = c.arrowStart && c.arrowEnd === false;
    const arrow = c.lineStyle === 'dotted' ? (!reverse && c.arrowStart ? '<-.->' : c.arrowEnd === false && !reverse ? '-.-' : '-.->') : c.lineStyle === 'thick' ? (!reverse && c.arrowStart ? '<==>' : c.arrowEnd === false && !reverse ? '===' : '==>') : reverse ? '-->' : c.arrowStart ? '<-->' : c.arrowEnd === false ? '---' : '-->';
    return `    ${names.get(reverse ? c.to : c.from)} ${arrow}${c.text ? `|"${encodeLabel(c.text)}"|` : ''} ${names.get(reverse ? c.from : c.to)}`;
  })].join('\n');
  if (result.length > 100000) throw new Error('Converted Mermaid exceeds 100,000 characters. Export JSON to preserve the complete diagram.');
  return result;
}

// Position, dimensions, colors and routing stay native; Mermaid describes the
// graph, not pixel layout. Compare semantics, not source formatting or order.
export function graphSignature(doc) {
  return JSON.stringify({ nodes: doc.elements.map(e => [e.id, e.type, e.text ?? '', e.group ?? '']).sort(), edges: doc.connectors.map(c => {
    const reverse = c.arrowStart && c.arrowEnd === false;
    return [reverse ? c.to : c.from, reverse ? c.from : c.to, c.text || '', !reverse && !!c.arrowStart, reverse || c.arrowEnd !== false, c.lineStyle || 'solid'];
  }).sort() });
}
export function mermaidSynchronized(doc) {
  if (!doc.mermaid || doc.elements.some(e => !SHAPES.some(s => s[2] === e.type))) return false;
  try { return graphSignature(fromMermaid(doc.mermaid)) === graphSignature(fromMermaid(toMermaid(doc.elements, doc.connectors, doc.mermaid))); } catch { return false; }
}
export function syncCanvasMermaid(prev, next) {
  if (prev.mermaid !== next.mermaid || graphSignature(prev) === graphSignature(next) || !mermaidSynchronized(prev)) return next;
  if (next.elements.some(e => !SHAPES.some(s => s[2] === e.type))) return next;
  try { return { ...next, mermaid: toMermaid(next.elements, next.connectors, prev.mermaid) }; } catch { return next; }
}
